import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { CountUp } from './CountUp';
import { Delta } from './Delta';
import { Reveal } from './Reveal';
import { Sparkline } from './Sparkline';
import { series, YFormat } from './palette';

export type Stat = {
  label: string;
  value: number;
  previous?: number;
  lowerIsBetter?: boolean;
  format?: YFormat;
  caption?: React.ReactNode;
  /** Trend points for the sparkline. */
  trend?: number[];
  color?: string;
  /** In-app route. */
  to?: string;
};

// Headline figure with count-up, delta chip, caption and optional sparkline.
// Ported from splitfin-manager src/components/dash/stat-card.tsx.
export const StatCard: React.FC<{ stat: Stat; index?: number; className?: string }> = ({ stat, index = 0, className }) => {
  const color = stat.color ?? series.green;
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="truncate text-[12px] font-medium text-[color:var(--oa-muted)]">{stat.label}</p>
        <Delta value={stat.value} previous={stat.previous} lowerIsBetter={stat.lowerIsBetter} />
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="shrink-0 whitespace-nowrap font-montserrat text-[28px] font-extrabold leading-none tracking-[-0.02em] text-[color:var(--oa-text)] tabular-nums">
          <CountUp value={stat.value} format={stat.format} />
        </p>
        {stat.trend && stat.trend.length > 1 ? <Sparkline points={stat.trend} color={color} className="min-w-0 max-w-28 flex-1" height={36} /> : null}
      </div>
      <p className="mt-2 min-h-4 truncate text-[11.5px] text-[color:var(--oa-muted)]">{stat.caption ?? (stat.previous !== undefined ? 'vs previous period' : ' ')}</p>
    </>
  );
  const classes = cn(
    'relative block rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] p-4 transition-[border-color,box-shadow] duration-200',
    stat.to && 'hover:border-[color:var(--oa-line-3)] hover:shadow-[0_2px_10px_-4px_rgba(11,15,25,0.12)]',
    className
  );
  return (
    <Reveal index={index}>
      {stat.to ? <Link to={stat.to} className={classes}>{inner}</Link> : <div className={classes}>{inner}</div>}
    </Reveal>
  );
};

// Row of stat cards: scrolls sideways on phones, fills the width on desktop.
export const StatRow: React.FC<{ stats: Stat[]; startIndex?: number; className?: string }> = ({ stats, startIndex = 0, className }) => (
  <section
    aria-label="Key figures"
    className={cn(
      '-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0',
      stats.length >= 4 && 'lg:grid-cols-4',
      stats.length >= 5 && 'xl:grid-cols-5',
      className
    )}
  >
    {stats.map((s, i) => (
      <StatCard key={s.label} stat={s} index={startIndex + i} className="w-[68vw] shrink-0 snap-start sm:w-[46vw] md:w-auto" />
    ))}
  </section>
);
